import { useAtomValue } from 'jotai';
import { useMemo,useState } from 'react';

import { ActionItemOperation,ActionItemStatus } from '@ekhodealer/ekho-common';
import { Button, Divider,Dropdown, Group, SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { EmptySection } from '@common/components/EmptySection';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useGetOrderProgress } from '../../api/useGetOrderProgress';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';

import { ProgressSection } from './ProgressSection';

/**
 * Labels for each operation type
 */
const OPERATION_LABELS: Record<ActionItemOperation, string> = {
	[ActionItemOperation.PREORDER]: 'Preorder',
	[ActionItemOperation.PAYMENT]: 'Payment',
	[ActionItemOperation.SECURITY]: 'Security',
	[ActionItemOperation.FINANCING]: 'Financing',
	[ActionItemOperation.INSURANCE]: 'Insurance',
	[ActionItemOperation.VEHICLE_PREPARATION]: 'Vehicle Preparation',
	[ActionItemOperation.TAR]: 'Titling & Registration',
	[ActionItemOperation.FULFILLMENT]: 'Fulfillment',
};

export const ProgressTabContent = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderProgressResponse, isLoading } = useGetOrderProgress(
		selectedRow?.purchasedVehicleId || undefined,
	);
	const { data: currentServicesProvider } = useCurrentServicesProvider();

	// Extract progress from response
	const orderProgress = orderProgressResponse?.progress;

	// Filter state
	const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
	const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
	const [selectedOperation, setSelectedOperation] = useState<string | null>(null);

	// Build agent options from participants returned by backend
	const agentOptions = useMemo(() => {
		if (!orderProgressResponse) return [];
		const participantsList = orderProgressResponse.participants || [];
		return participantsList.map((participant) => ({
			id: participant.agentId,
			label: participant.agentName,
		}));
	}, [orderProgressResponse]);

	const currentServicesProviderId = currentServicesProvider?.id;

	// Predefined options for "Status" dropdown
	const statusOptions = useMemo(
		() => [
			{
				id: ActionItemStatus.QUEUED,
				label: 'Queued',
			},
			{
				id: ActionItemStatus.PENDING,
				label: 'Pending',
			},
			{
				id: ActionItemStatus.COMPLETE,
				label: 'Completed',
			},
		],
		[],
	);

	// Predefined options for "Operations" dropdown
	const operationOptions = useMemo(() => {
		const operations: ActionItemOperation[] = [
			ActionItemOperation.PREORDER,
			ActionItemOperation.PAYMENT,
			ActionItemOperation.SECURITY,
			ActionItemOperation.FINANCING,
			ActionItemOperation.INSURANCE,
			ActionItemOperation.VEHICLE_PREPARATION,
			ActionItemOperation.TAR,
			ActionItemOperation.FULFILLMENT,
		];

		return operations.map((operation) => ({
			id: operation,
			label: OPERATION_LABELS[operation],
		}));
	}, []);

	// Check if individual filters differ from defaults
	const hasAgentFilter = selectedAgent !== null;
	const hasStatusFilter = selectedStatus !== null;
	const hasOperationFilter = selectedOperation !== null;

	// Check if any filters differ from defaults
	const hasActiveFilters = useMemo(() => {
		return hasAgentFilter || hasStatusFilter || hasOperationFilter;
	}, [hasAgentFilter, hasStatusFilter, hasOperationFilter]);

	// Handler to reset all filters to defaults
	const handleClearAllFilters = () => {
		setSelectedAgent(null);
		setSelectedStatus(null);
		setSelectedOperation(null);
	};

	// Calculate "Your pending tasks" - always filtered by pending + current services provider
	const yourPendingTasks = useMemo(() => {
		const result: Record<string, ActionItem[]> = {};

		// Return empty if no data or no current services provider
		if (!orderProgress || !currentServicesProviderId) {
			return result;
		}

		// Iterate through each category
		Object.entries(orderProgress).forEach(([category, items]) => {
			const filtered = items.filter(
				(item) =>
					item.status === ActionItemStatus.PENDING &&
					item.agentId === currentServicesProviderId,
			);
			result[category] = filtered;
		});

		return result;
	}, [orderProgress, currentServicesProviderId]);

	// Calculate total pending tasks for the current user
	const totalPendingTasks = useMemo(() => {
		return Object.values(yourPendingTasks).reduce((sum, items) => sum + items.length, 0);
	}, [yourPendingTasks]);

	// Apply filters to order progress
	const filteredProgress = useMemo(() => {
		const result: Record<string, ActionItem[]> = {};

		// Return empty if no data
		if (!orderProgress) {
			return result;
		}

		// Iterate through each category
		Object.entries(orderProgress).forEach(([category, items]) => {
			let filtered = [...items];

			// Apply agent filter by agentId
			if (selectedAgent) {
				filtered = filtered.filter((item) => item.agentId === selectedAgent);
			}

			// Apply status filter
			if (selectedStatus) {
				filtered = filtered.filter((item) => item.status === selectedStatus);
			}

			// Apply operation filter (filter by category)
			if (selectedOperation && category !== selectedOperation) {
				filtered = [];
			}

			result[category] = filtered;
		});

		return result;
	}, [orderProgress, selectedAgent, selectedStatus, selectedOperation]);

	// Calculate total filtered items for empty state
	const totalFilteredItems = useMemo(() => {
		return Object.values(filteredProgress).reduce((sum, items) => sum + items.length, 0);
	}, [filteredProgress]);

	return (
		<Stack itemsSpacing='24' padding={{ top: '12' }}>
			{/* Your Pending Tasks section */}
			<Stack itemsSpacing='12'>
				<Divider label='Your pending tasks' />

				{isLoading ? (
					<SkeletonRect width='100%' height={160} />
				) : totalPendingTasks === 0 ? (
					<EmptySection message='You currently have no pending tasks for this order' />
				) : (
					<>
						{/* Iterate through all operation types in order for pending tasks */}
						{Object.values(ActionItemOperation).map((operation) => {
							const items = yourPendingTasks[operation] || [];
							if (items.length === 0) return null;

							return (
								<ProgressSection
									key={`pending-${operation}`}
									title={OPERATION_LABELS[operation]}
									actionItems={items}
									participants={orderProgressResponse?.participants || []}
								/>
							);
						})}
					</>
				)}
			</Stack>

			{/* All Tasks section */}
			<Stack itemsSpacing='24'>
				<Divider label='All tasks' />

				{isLoading ? (
					<SkeletonRect width='100%' height={160} />
				) : (
					<>
						{/* Filter dropdowns */}
						<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
							<Group itemsSpacing='8' itemsAlignX='start' noWrap>
								{/* Only show "Assigned to" dropdown if there are participants */}
								{agentOptions.length > 0 && (
									<Dropdown
										options={agentOptions}
										value={selectedAgent}
										size='medium'
										buttonVariant={hasAgentFilter ? 'secondary' : undefined}
										onSelectionChange={(value) =>
											setSelectedAgent(value || null)
										}
										placeholder='Assigned to'
										isClearable
									/>
								)}
								<Dropdown
									options={statusOptions}
									value={selectedStatus}
									size='medium'
									buttonVariant={hasStatusFilter ? 'secondary' : undefined}
									onSelectionChange={(value) => setSelectedStatus(value || null)}
									placeholder='Status'
									isClearable
								/>
								<Dropdown
									options={operationOptions}
									value={selectedOperation}
									size='medium'
									buttonVariant={hasOperationFilter ? 'secondary' : undefined}
									onSelectionChange={(value) =>
										setSelectedOperation(value || null)
									}
									placeholder='Category'
									isClearable
								/>
								{hasActiveFilters && (
									<Button
										variant='ghost'
										size='medium'
										onPress={handleClearAllFilters}>
										Clear filters
									</Button>
								)}
							</Group>
						</Group>

						{/* Show empty state if no items match */}
						{totalFilteredItems === 0 ? (
							<EmptySection message='No action items match filters' />
						) : (
							<>
								{/* Progress sections - iterate through all operation types in order */}
								{Object.values(ActionItemOperation).map((operation) => {
									const items = filteredProgress[operation] || [];
									if (items.length === 0) return null;

									return (
										<ProgressSection
											key={operation}
											title={OPERATION_LABELS[operation]}
											actionItems={items}
											participants={orderProgressResponse?.participants || []}
										/>
									);
								})}
							</>
						)}
					</>
				)}
			</Stack>
		</Stack>
	);
};
